/*!
 * screenFx.js — screen(): cheap effects on everything drawn
 * © 2026 Rob Canning — GPLv3
 *
 *     screen(kaleido:6)              the stage in 6 mirrored wedges
 *     screen(kaleido:8, turn:45)     ... the wedge taken from 45° (0 is up)
 *     screen(mirror:x)               the left half, mirrored onto the right
 *     screen(mirror:y)               the top half, mirrored onto the bottom
 *     screen(tile:3)                 the whole stage, 3 x 3 times
 *     screen(hue:120)                colours turned round the wheel (degrees)
 *     screen(invert:1)               negative (0..1)
 *     screen(contrast:1.6, saturate:2, bright:1.2)
 *     screen(blur:3)                 soft (px)
 *     screen(pixel:12)               in blocks of 12 px
 *     screen(warp:30)                rippled, as through moving water: 30 px at most
 *     screen(warp:30, grain:300)     ... in bigger ripples (px; 150 by default)
 *     screen(split:6)                red and blue pulled 6 px apart from green
 *     screen(off)                    all of it back to the plain stage
 *     screen(clear)                  every trail wiped (scopes', shapes'), once --
 *                                    screen(clear, trig:every(4, s1.hit)) on every 4th note
 *
 * A screen(...) changes only what it names; 0 (or off) turns one off. Any
 * value can follow a signal: screen(hue:follow(bass.level, 0, 360)),
 * screen(kaleido:6, turn:follow(beat.phase, 0, 360)). One of kaleido /
 * mirror / tile at a time (in that order); the filters go on top of it.
 *
 * Cheap: no pixels are read. kaleido / mirror / tile are live SVG copies of
 * the stage (<use> of the score's own SVG), each clipped and turned into
 * place, over the hidden original -- vector, the stage drawn N times (fine
 * for a live-coding stage, heavy on a long notated score). The filters are
 * CSS on the score's container; pixel is a small SVG filter.
 */

import { isMoving, bindParam, releaseBinding } from "../control/paramBinding.js";
import { maskFromArgs } from "./stageMask.js";
import { secPerBeat } from "../cues/beats.js";
import { onReset } from "./teardown.js";

const NS = "http://www.w3.org/2000/svg";
const KEYS = ["kaleido", "turn", "mirror", "tile", "hue", "invert", "contrast", "saturate", "bright", "blur", "pixel", "warp", "grain", "split"];
const state = {};                  // key -> the value now
const passing = {};                // key -> an amount on top, for a while: a scene's transition (setPassing)
const fx = k => num(state[k]) + num(passing[k]);   // what shows: the setting and a transition's amount
let overlay = null, raf = 0, layoutKey = "";
let holders = [];                  // each copy's group: the stage's <use>, and its scopes' canvases

const stageSvg = () => document.getElementById("score") || document.querySelector("#scoreContainer svg");

/**
 * The stage, as the copies' <use> names it: the score's svg (#score), or
 * the stage that has no score -- Oscilla Live's, given an id here. With
 * #score written in, Live's copies named nothing: its shapes and texts were
 * gone from a kaleido / mirror / tile, and only the scopes' canvases (copied
 * apart, below) showed.
 */
function stageRef() {
  const svg = stageSvg();
  if (svg && !svg.id) svg.id = "oscilla-stage";
  return `#${svg?.id || "score"}`;
}
const container = () => document.getElementById("scoreContainer");
const num = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);

// ---- the filters ---------------------------------------------------------

/** The SVG filters screen() uses, made once. */
function screenDefs() {
  let defs = document.getElementById("oscilla-screen-defs");
  if (defs) return defs;
  defs = document.createElementNS(NS, "svg");
  defs.id = "oscilla-screen-defs";
  defs.setAttribute("style", "position:absolute;width:0;height:0;overflow:hidden");
  // warp: the stage pushed about by noise (red: across, green: down), the
  // noise drifting round a circle (warpFrame) -- made over a margin wider
  // than the drift, so the stage is always over noise
  // split: red, green and blue taken apart, red and blue moved, added back
  // Both over the stage's own colour (stageFill): what is moved in from
  // past its edge is that, not the page's -- a wavy frame, coloured bars
  const ch = (r, g, b) => `${r} 0 0 0 0  0 ${g} 0 0 0  0 0 ${b} 0 0  0 0 0 1 0`;
  defs.innerHTML = `<filter id="oscilla-pixel" x="0" y="0" width="1" height="1" color-interpolation-filters="sRGB">
      <feFlood x="0" y="0" width="1" height="1" result="dot"/>
      <feComposite in="dot" width="8" height="8" result="cell"/>
      <feTile in="cell" result="grid"/>
      <feComposite in="SourceGraphic" in2="grid" operator="in"/>
      <feMorphology operator="dilate" radius="4"/>
    </filter>
    <filter id="oscilla-warp" x="-0.15" y="-0.15" width="1.3" height="1.3" color-interpolation-filters="sRGB">
      <feTurbulence type="fractalNoise" baseFrequency="0.0067" numOctaves="2" seed="7" result="noise"/>
      <feOffset in="noise" dx="0" dy="0" result="drift"/>
      <feDisplacementMap in="SourceGraphic" in2="drift" scale="0" xChannelSelector="R" yChannelSelector="G" result="moved"/>
      <feFlood class="stage-fill" flood-opacity="0" result="fill"/>
      <feMerge><feMergeNode in="fill"/><feMergeNode in="moved"/></feMerge>
    </filter>
    <filter id="oscilla-split" x="-0.05" y="0" width="1.1" height="1" color-interpolation-filters="sRGB">
      <feFlood class="stage-fill" flood-opacity="0" result="fill"/>
      <feOffset in="SourceGraphic" dx="0" result="left"/>
      <feMerge result="rs"><feMergeNode in="fill"/><feMergeNode in="left"/></feMerge>
      <feColorMatrix in="rs" type="matrix" values="${ch(1, 0, 0)}" result="r2"/>
      <feColorMatrix in="SourceGraphic" type="matrix" values="${ch(0, 1, 0)}" result="g"/>
      <feOffset in="SourceGraphic" dx="0" result="right"/>
      <feMerge result="bs"><feMergeNode in="fill"/><feMergeNode in="right"/></feMerge>
      <feColorMatrix in="bs" type="matrix" values="${ch(0, 0, 1)}" result="b2"/>
      <feComposite in="r2" in2="g" operator="arithmetic" k2="1" k3="1" result="rg"/>
      <feComposite in="rg" in2="b2" operator="arithmetic" k2="1" k3="1"/>
    </filter>`;
  document.body.appendChild(defs);
  return defs;
}

function pixelFilter(size) {
  const defs = screenDefs();
  // one dot of the picture per cell, grown to fill the cell
  const s = Math.max(2, Math.round(size)), h = Math.floor(s / 2);
  const f = defs.querySelector("#oscilla-pixel");
  f.querySelector("feFlood").setAttribute("x", h);
  f.querySelector("feFlood").setAttribute("y", h);
  const comp = f.querySelector("feComposite[in=dot]");
  comp.setAttribute("width", s); comp.setAttribute("height", s);
  f.querySelector("feMorphology").setAttribute("radius", h);
}

function applyFilters() {
  const c = container();
  if (!c) return;
  const parts = [];
  if (fx("hue")) parts.push(`hue-rotate(${fx("hue")}deg)`);
  if (fx("invert")) parts.push(`invert(${Math.min(1, Math.max(0, fx("invert")))})`);
  if (state.contrast != null && num(state.contrast, 1) !== 1) parts.push(`contrast(${Math.max(0, num(state.contrast, 1))})`);
  if (state.saturate != null && num(state.saturate, 1) !== 1) parts.push(`saturate(${Math.max(0, num(state.saturate, 1))})`);
  if (state.bright != null && num(state.bright, 1) !== 1) parts.push(`brightness(${Math.max(0, num(state.bright, 1))})`);
  if (fx("blur") > 0) parts.push(`blur(${fx("blur")}px)`);
  if (fx("pixel") >= 2) { pixelFilter(fx("pixel")); parts.push("url(#oscilla-pixel)"); }
  // warp and split move the picture: before the colour ones, which then
  // tint what was moved
  const warp = Math.abs(fx("warp")), split = fx("split");
  if (split || warp > 0) stageFill();
  if (split) { splitFilter(split); parts.unshift("url(#oscilla-split)"); }
  if (warp > 0) { warpFilter(warp); parts.unshift("url(#oscilla-warp)"); }
  if (warp > 0 && !warpRaf) warpRaf = requestAnimationFrame(warpFrame);
  if (flashes.invert > 0) parts.push("invert(1)");          // a text's flash:invert (stageFlash)
  c.style.filter = parts.join(" ");
}

/** warp: how far, and how big the ripples (grain:, px). */
function warpFilter(amount) {
  const f = screenDefs().querySelector("#oscilla-warp");
  f.querySelector("feDisplacementMap").setAttribute("scale", String(Math.min(400, amount)));
  const grain = Math.max(8, num(state.grain, 150) || 150);
  f.querySelector("feTurbulence").setAttribute("baseFrequency", String(1 / grain));
}

/** The noise drifts round a circle, once every 8 beats: the ripples flow. */
let warpRaf = 0;
function warpFrame(now) {
  warpRaf = 0;
  if (!(Math.abs(fx("warp")) > 0)) return;
  const f = document.querySelector("#oscilla-warp feOffset");
  const c = container();
  if (f && c) {
    // a little inside the margin the noise is made over (15% each side)
    const r = 0.1 * Math.min(c.clientWidth, c.clientHeight);
    const a = (2 * Math.PI * now) / (8000 * secPerBeat());
    f.setAttribute("dx", (r * Math.cos(a)).toFixed(1));
    f.setAttribute("dy", (r * Math.sin(a)).toFixed(1));
  }
  warpRaf = requestAnimationFrame(warpFrame);
}

/** split: red one way and blue the other, by px. */
function splitFilter(px) {
  const f = screenDefs().querySelector("#oscilla-split");
  const d = Math.max(-200, Math.min(200, px));
  const [r, b] = f.querySelectorAll("feOffset");
  r.setAttribute("dx", String(-d));
  b.setAttribute("dx", String(d));
}

/**
 * What warp and split put under what they move: the colour the stage is
 * painted (the container's own background, which the filter moves too),
 * or nothing when the page shows through it.
 */
function stageFill() {
  const c = container();
  const bg = c ? getComputedStyle(c).backgroundColor : "";
  const solid = bg && bg !== "transparent" && !/rgba\([^)]*,\s*0\)$/.test(bg);
  for (const f of screenDefs().querySelectorAll("feFlood.stage-fill")) {
    if (f.getAttribute("flood-color") !== bg) f.setAttribute("flood-color", solid ? bg : "black");
    f.setAttribute("flood-opacity", solid ? "1" : "0");
  }
}

// ---- kaleido / mirror / tile: copies of the stage -------------------------

/** Which copying is on: { mode, n } or null. */
function geometry() {
  const k = Math.round(num(state.kaleido));
  if (k >= 2) return { mode: "kaleido", n: Math.min(24, k) };
  const m = String(state.mirror ?? "").toLowerCase();
  if (m === "x" || m === "y" || m === "xy") return { mode: "mirror", n: m };
  const t = Math.round(num(state.tile));
  if (t >= 2) return { mode: "tile", n: Math.min(8, t) };
  return null;
}

function el(tag, attrs = {}, parent = null) {
  const e = document.createElementNS(NS, tag);
  for (const k in attrs) e.setAttribute(k, attrs[k]);
  parent?.appendChild(e);
  return e;
}

/** Build the copies for this mode and size (once per change of either). */
function build(g, W, H) {
  overlay.innerHTML = "";
  holders = [];
  const defs = el("defs", {}, overlay);
  const cx = W / 2, cy = H / 2, R = Math.hypot(W, H);
  const copy = (parent, clip) => {
    const holder = el("g", clip ? { "clip-path": `url(#${clip})` } : {}, parent);
    holders.push(holder);
    return el("use", { href: stageRef(), class: "oscilla-screen-copy" }, holder);
  };
  if (g.mode === "kaleido") {
    const n = g.n, half = Math.PI / n;
    const turn = (num(state.turn) - 90) * Math.PI / 180;     // 0: the wedge points up
    const p = a => `${cx + R * Math.cos(a)},${cy + R * Math.sin(a)}`;
    el("path", { d: `M${cx},${cy} L${p(turn - half)} L${p(turn + half)} Z` },
      el("clipPath", { id: "oscilla-wedge", clipPathUnits: "userSpaceOnUse" }, defs));
    const axis = turn * 180 / Math.PI;
    for (let k = 0; k < n; k++) {
      // every other wedge mirrored across its axis: the edges meet
      const flip = k % 2 ? ` rotate(${axis} ${cx} ${cy}) translate(${cx} ${cy}) scale(1 -1) translate(${-cx} ${-cy}) rotate(${-axis} ${cx} ${cy})` : "";
      copy(el("g", { transform: `rotate(${k * 360 / n} ${cx} ${cy})${flip}` }, overlay), "oscilla-wedge");
    }
  } else if (g.mode === "mirror") {
    const x = g.n.includes("x"), y = g.n.includes("y");
    el("rect", { x: 0, y: 0, width: x ? cx : W, height: y ? cy : H },
      el("clipPath", { id: "oscilla-half", clipPathUnits: "userSpaceOnUse" }, defs));
    const flips = [""];
    if (x) flips.push(`translate(${W} 0) scale(-1 1)`);
    if (y) flips.push(`translate(0 ${H}) scale(1 -1)`);
    if (x && y) flips.push(`translate(${W} ${H}) scale(-1 -1)`);
    for (const t of flips) copy(el("g", { transform: t }, overlay), "oscilla-half");
  } else {
    // each copy clipped to what the window shows: the stage runs on past it
    // (a long score, below and to the right), and a copy left whole ran into
    // the cell under it -- whose own background, painted after, cut it off
    const n = g.n;
    el("rect", { x: 0, y: 0, width: W, height: H },
      el("clipPath", { id: "oscilla-cell", clipPathUnits: "userSpaceOnUse" }, defs));
    for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
      copy(el("g", { transform: `translate(${i * W / n} ${j * H / n}) scale(${1 / n})` }, overlay), "oscilla-cell");
    }
  }
}

/**
 * The CSS filters between a scope's canvas and the stage, innermost first.
 * Dark mode inverts the page and un-inverts the sound boxes (livecode.css):
 * a copy outside the box gets only the first, and a white trace turns black.
 */
function filtersAbove(c, src) {
  const out = [];
  for (let e = c; e && e !== src; e = e.parentElement) {
    const f = getComputedStyle(e).filter;
    if (f && f !== "none") out.push(f);
  }
  return out.join(" ");
}

/**
 * The scopes draw on canvases, and a <use> copy of a canvas is blank: each
 * copy gets canvases of its own, painted from the scopes every frame
 * (canvas to canvas: cheap), under the copy's own clip and turn.
 */
function copyScopes(src, cr) {
  const scopes = [...src.querySelectorAll("foreignObject canvas")].filter(c => c.width && c.height);
  const filters = scopes.map(c => filtersAbove(c, src));
  for (const holder of holders) {
    let mirrors = [...holder.querySelectorAll("foreignObject.oscilla-screen-scope")];
    while (mirrors.length > scopes.length) mirrors.pop().remove();
    while (mirrors.length < scopes.length) {
      const fo = el("foreignObject", { class: "oscilla-screen-scope" }, holder);
      fo.appendChild(document.createElement("canvas")).style.cssText = "width:100%;height:100%;display:block";
      mirrors.push(fo);
    }
    scopes.forEach((c, i) => {
      const fo = mirrors[i], cv = fo.firstChild, b = c.getBoundingClientRect();
      fo.setAttribute("x", b.left - cr.left); fo.setAttribute("y", b.top - cr.top);
      fo.setAttribute("width", b.width); fo.setAttribute("height", b.height);
      const f = filters[i];
      if (cv.style.filter !== f) cv.style.filter = f;
      if (cv.width !== c.width || cv.height !== c.height) { cv.width = c.width; cv.height = c.height; }
      const g = cv.getContext("2d");
      g.clearRect(0, 0, cv.width, cv.height);
      g.drawImage(c, 0, 0);
    });
  }
}

/** Each frame: the copies follow the stage as it scrolls and zooms. */
function frame() {
  raf = 0;
  const g = geometry();
  const c = container(), src = stageSvg();
  if (!g || !c || !src) return;
  const cr = c.getBoundingClientRect(), r = src.getBoundingClientRect();
  const key = `${g.mode}|${g.n}|${Math.round(cr.width)}x${Math.round(cr.height)}|${g.mode === "kaleido" ? num(state.turn) : ""}`;
  if (key !== layoutKey) { build(g, cr.width, cr.height); layoutKey = key; }
  const x = r.left - cr.left, y = r.top - cr.top;
  for (const u of overlay.querySelectorAll(".oscilla-screen-copy")) {
    u.setAttribute("x", x); u.setAttribute("y", y);
    u.setAttribute("width", r.width); u.setAttribute("height", r.height);
  }
  copyScopes(src, cr);
  raf = requestAnimationFrame(frame);
}

function applyGeometry() {
  const g = geometry();
  const c = container(), src = stageSvg(), inner = document.getElementById("scoreInner");
  if (!g || !c || !src) {
    overlay?.remove(); overlay = null; layoutKey = "";
    if (inner) inner.style.opacity = "";
    cancelAnimationFrame(raf); raf = 0;
    return;
  }
  // what is drawn outside the score's own box (the console's shapes and
  // scopes) is in the copies too
  src.setAttribute("overflow", "visible");
  if (!overlay) {
    overlay = document.createElementNS(NS, "svg");
    overlay.id = "oscilla-screen";
    overlay.setAttribute("style", "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;overflow:hidden;z-index:2");
    if (getComputedStyle(c).position === "static") c.style.position = "relative";
    c.appendChild(overlay);
  }
  if (inner) inner.style.opacity = "0";            // the copies are what is seen
  if (!raf) raf = requestAnimationFrame(frame);
}

// ---- the cue ------------------------------------------------------------

function setKey(k, v) {
  state[k] = v;
  if (["kaleido", "turn", "mirror", "tile"].includes(k)) { if (k !== "turn") layoutKey = ""; applyGeometry(); }
  else applyFilters();
  if (k === "turn") layoutKey = "";
}

// ---- a text's flash: (text.js) -- the stage, not the page ------------------

const flashes = { invert: 0, colours: [] };     // what is flashing now: counts, the colours in order
let stageBgWas = null;                          // { container, rect } as they were

/** The score's own background: Inkscape's Background layer, or a #background rect. */
const backgroundRect = () => stageSvg()?.querySelector("#background, #layer-background rect") ?? null;

function paintStageBg() {
  const c = container(), rect = backgroundRect();
  const colour = flashes.colours[flashes.colours.length - 1];
  if (colour) {
    if (!stageBgWas) stageBgWas = { container: c?.style.backgroundColor ?? "", rect: rect?.getAttribute("fill"), el: rect };
    if (c) c.style.backgroundColor = colour;
    rect?.setAttribute("fill", colour);
  } else if (stageBgWas) {
    if (c) c.style.backgroundColor = stageBgWas.container;
    if (stageBgWas.el) stageBgWas.el.setAttribute("fill", stageBgWas.rect ?? "");
    stageBgWas = null;
  }
  if (num(state.warp) || num(state.split)) stageFill();    // what they put under it
}

/**
 * The stage flashes while a word shows: flash:invert -- inverted, as
 * screen(invert:1) does it -- or flash:white, "#f0f": its background that
 * colour (behind everything on it). Only the stage: the console, the bars
 * and a word on top stay as they are. Returns the release, safe to call twice.
 */
export function stageFlash(kind) {
  if (!kind) return () => {};
  const invert = /^invert$/i.test(kind);
  if (invert) { flashes.invert++; applyFilters(); }
  else { flashes.colours.push(kind); paintStageBg(); }
  let done = false;
  return () => {
    if (done) return;
    done = true;
    if (invert) { flashes.invert = Math.max(0, flashes.invert - 1); applyFilters(); }
    else { const i = flashes.colours.lastIndexOf(kind); if (i >= 0) flashes.colours.splice(i, 1); paintStageBg(); }
  };
}

export function screenOff() {
  for (const k of KEYS) { releaseBinding(`screen:${k}`); delete state[k]; }
  applyGeometry();
  applyFilters();
  const c = container();
  if (c) c.style.filter = "";
}

/**
 * Every trail wiped: the canvases scopes and shapes draw on, in the score
 * and in the screen's copies. What is still drawing paints again next frame.
 */
export function clearTrails() {
  // the boxes' (in the score) and the texts' (on the page: text.js .cue-text-trail)
  for (const c of document.querySelectorAll("#scoreContainer foreignObject canvas, canvas.cue-text-trail, .cue-text-trail canvas")) {
    c.getContext("2d")?.clearRect(0, 0, c.width, c.height);
  }
}
onReset("stage", screenOff);
onReset("stage", clearTrails);

/**
 * An amount on top of a filter's setting, for a while: a scene's transition
 * through blur, warp, pixel, hue, invert or split (liveScenes.js) swells and
 * settles here, whatever screen() has set (a follow() goes on writing its
 * own). 0 takes it off.
 */
export const PASSING_KEYS = ["blur", "warp", "pixel", "hue", "invert", "split"];
export function setPassing(k, v) {
  if (!PASSING_KEYS.includes(k)) return;
  if (num(v)) passing[k] = num(v); else delete passing[k];
  applyFilters();
}

/** screen(...) as a cue. */
export function handleScreenCue(ast) {
  const args = ast?.args || [];
  if (!args.length) { console.warn("[screen] screen(kaleido:6) / screen(hue:120) / screen(off) / screen(clear)"); return; }
  if (args.some(a => a.type === "off" || a.type === "stop")) { screenOff(); return; }
  if (args.some(a => a.type === "clear")) clearTrails();
  // mask:spot / hole:spot -- the whole stage through a shape, or with it cut out (stageMask.js)
  maskFromArgs("stage", () => [container()].filter(Boolean), k => args.find(a => a.type === k)?.value);
  for (const a of args) {
    if (a.type === "clear" || a.type === "trig" || a.type === "mask" || a.type === "hole") continue;
    if (!KEYS.includes(a.type)) { console.warn(`[screen] ${a.type}: one of ${KEYS.join(", ")}`); continue; }
    const owner = `screen:${a.type}`;
    if (isMoving(a.value)) bindParam(a.value, v => setKey(a.type, v), { owner, min: -1e6, max: 1e6, default: 0, rest: () => setKey(a.type, 0) });
    else {
      releaseBinding(owner);
      const v = String(a.value).toLowerCase() === "off" ? 0 : a.value;
      setKey(a.type, v);
    }
  }
}
